import { useState, useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "motion/react";
import { ResultOf, readFragment } from "gql.tada";
import { QuotationCarouselSectionById } from "@queries/entries/QuotationCarouselSectionById";
import { QuotationCarouselSectionFragment } from "@queries/fragments/QuotationCarouselSectionFragment";
import { AnimatedSplitLines } from "@components/shared/AnimatedSplitLines";
import { Button } from "@components/shared/core/Button";

type Quotation = NonNullable<
  NonNullable<
    ResultOf<typeof QuotationCarouselSectionFragment>["quotationsCollection"]
  >["items"][number]
>;

const SECTION_CLASSNAME =
  "set-current-palette-blue relative py-20 dsk:py-[150px] px-6 dsk:px-[90px] flex items-center justify-center min-h-[600px]";

const ArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="17"
    height="16"
    fill="none"
    aria-hidden
  >
    <path
      d="m8.5 1 7 7-7 7M14.833 8H1.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const QuoteSlide = ({
  quote,
  name,
  position,
  company,
}: {
  quote: string;
  name: string | null;
  position: string | null;
  company: string | null;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const value = useTransform(scrollYProgress, [0, 0.5], [-1, 0]);
  const attributionOpacity = useTransform(scrollYProgress, [0.4, 0.6], [0, 1]);

  const attributionLine = [position, company].filter(Boolean).join(", ");

  return (
    <div
      ref={ref}
      className="flex flex-col items-center gap-[50px] w-full"
    >
      <p className="text-palette-current-highlight text-body-feature text-center max-w-[1094px] max-[499px]:text-[24px] max-[499px]:tracking-[-0.48px]">
        <AnimatedSplitLines
          value={value}
          animationType="clipPathReveal"
          center
          underlyingTextClassNames="text-palette-current-deep"
        >
          {quote}
        </AnimatedSplitLines>
      </p>
      <motion.div
        style={{ opacity: attributionOpacity }}
        className="text-neutral-cream text-body-large text-center max-w-[298px]"
      >
        {name && <p>{name}</p>}
        {attributionLine && <p>{attributionLine}</p>}
      </motion.div>
    </div>
  );
};

export const QuotationCarouselSection = ({
  entry: { quotationCarouselSection: entry },
}: {
  entry: ResultOf<typeof QuotationCarouselSectionById>;
}) => {
  const section = entry ? readFragment(QuotationCarouselSectionFragment, entry) : null;
  const items: Quotation[] = (section?.quotationsCollection?.items ?? []).filter(
    (q): q is Quotation => q !== null
  );
  const [index, setIndex] = useState(0);

  if (items.length === 0) return null;

  if (items.length === 1) {
    const single = items[0];
    return (
      <section className={SECTION_CLASSNAME}>
        <QuoteSlide
          quote={single.quotation ?? ""}
          name={single.attributionName}
          position={single.attributionPosition}
          company={single.attributionCompany}
        />
      </section>
    );
  }

  const canGoBack = index > 0;
  const canGoForward = index < items.length - 1;
  const current = items[index];

  return (
    <section className={SECTION_CLASSNAME}>
      <AnimatePresence mode="wait">
        <motion.div
          key={current.sys.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: .25 }}
          className="w-full px-12 dsk:px-6"
        >
          <QuoteSlide
            quote={current.quotation ?? ""}
            name={current.attributionName}
            position={current.attributionPosition}
            company={current.attributionCompany}
          />
        </motion.div>
      </AnimatePresence>

      <div className="absolute left-4 dsk:left-[55px] top-1/2 -translate-y-1/2">
        <Button
          variant="light-on-blur"
          size="inherit"
          className="size-[42px] rotate-180"
          disabled={!canGoBack}
          onClick={() => canGoBack && setIndex(index - 1)}
          aria-label="Previous quotation"
        >
          <ArrowIcon />
        </Button>
      </div>
      <div className="absolute right-4 dsk:right-[55px] top-1/2 -translate-y-1/2">
        <Button
          variant="light-on-blur"
          size="inherit"
          className="size-[42px]"
          disabled={!canGoForward}
          onClick={() => canGoForward && setIndex(index + 1)}
          aria-label="Next quotation"
        >
          <ArrowIcon />
        </Button>
      </div>
    </section>
  );
};
